import React, { useCallback, useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Paper,
  Divider,
  Alert,
  useTheme,
  Tooltip,
  FormControl,
  Avatar,
  Chip,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import StoreIcon from '@mui/icons-material/Store';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { useDealerGroup } from '../../../../context/DealerGroupContext';
import { useUser } from '../../../../context/UserContext';
import { formatToLocalTime } from 'utils/dateUtils';
import DualListMAIQ from 'app/components/DualListMAIQ';
import { DealerType } from '../dealer/types';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { headerSaga } from 'app/pages/Header/saga';
import { headerActions, reducer, sliceKey } from 'app/pages/Header/slice';
import { selectDealersList } from 'app/pages/Header/selectors';

interface DealerGroupDetailModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

export const DealerGroupDetailModal: React.FC<DealerGroupDetailModalProps> = ({
  open,
  onClose,
  data,
  setRefreshKey,
}) => {
  const { editDealerGroup, deleteDealerGroup, isEditing, setIsEditing } =
    useDealerGroup();
  const { getUsersByDealerGroupId } = useUser();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [error, setError] = useState<string>('');

  const [dealerGroup, setDealerGroup] = useState(data);
  const [originalData, setOriginalData] = useState(data);
  const [dealersForGroup, setDealersForGroup] = useState<DealerType[]>([]);
  const [originalDealersForGroup, setOriginalDealersForGroup] = useState<
    DealerType[]
  >([]);
  const [isSame, setIsSame] = useState(true);

  // Users related to this dealer group
  const [groupUsers, setGroupUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string>('');

  // Inject header reducer and saga
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get dealers from header Redux state
  const dealersListDetails = useSelector(selectDealersList);
  const dealers = dealersListDetails.data?.body?.items || [];

  // Fetch users related to this dealer group
  const fetchGroupUsers = useCallback(async () => {
    console.log("data.id", data?._id);
    if (!data?._id) return;
    
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await getUsersByDealerGroupId(data._id);
      
      if (response?.body?.data) {
        setGroupUsers(response.body.data);
        console.log('Setting groupUsers:', response.body.data);
      } else {
        setGroupUsers([]);
        console.log('No data found, setting empty array');
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setUsersError(err?.message || 'Failed to fetch users');
      setGroupUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [data?._id]);

  useEffect(() => {
    if (open && data?._id) {
      fetchGroupUsers();
    }
  }, [open, data?._id]);

  useEffect(() => {
    // Only compare user-editable fields
    const isNameSame = dealerGroup.name === originalData.name;
    const isUrlSame =
      (dealerGroup.settings?.business_dashboard_url || '') ===
      (originalData.settings?.business_dashboard_url || '');
    const isDealersSame =
      Array.isArray(dealerGroup.dealers) && Array.isArray(originalData.dealers)
        ? dealerGroup.dealers.length === originalData.dealers.length &&
          dealerGroup.dealers.every(
            (id, idx) => id === originalData.dealers[idx],
          )
        : dealerGroup.dealers === originalData.dealers;
    setIsSame(isNameSame && isUrlSame && isDealersSame);
  }, [dealerGroup, originalData]);

  useEffect(() => {
    setDealerGroup(data);
    setOriginalData(data);
    // Clear any existing error when modal opens
    setError('');
  }, [data, setError]);

  useEffect(() => {
    if (open) {
      // Convert dealer IDs to dealer objects if needed
      if (Array.isArray(data.dealers)) {
        const dealerObjects = data.dealers.map(dealer => {
          // If dealer is already an object, return it
          if (typeof dealer === 'object' && dealer !== null && dealer._id) {
            return dealer;
          }
          // If dealer is an ID (string), find the matching dealer object
          if (typeof dealer === 'string') {
            return dealers.find(d => d._id === dealer) || null;
          }
          return null;
        }).filter(Boolean); // Remove any null values
        
        setDealersForGroup(dealerObjects);
        setOriginalDealersForGroup(dealerObjects);
      } else {
        setDealersForGroup([]);
        setOriginalDealersForGroup([]);
      }
    }
  }, [open, data.dealers, dealers]);

  useEffect(() => {
    if (open && isEditing) {
      dispatch(headerActions.requestDealers());
    }
  }, [isEditing, open]);

  const handleChange = e => {
    const { name, value } = e.target;
    setDealerGroup(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleEditSubmit = () => {
    setIsSubmitting(true);
    
    // Only send user-editable fields, exclude backend-generated fields
    const editPayload = {
      name: dealerGroup.name,
      dealers: dealerGroup.dealers,
      settings: dealerGroup.settings
    };
    
    editDealerGroup(dealerGroup._id, editPayload, () => {
      setIsSubmitting(false);
      setIsEditing(false);
      setRefreshKey(prev => prev + 1);
      onClose();
    });
    setOpenConfirm(false);
  };

  const handleDeleteDealerGroup = () => {
    setIsSubmitting(true);
    deleteDealerGroup(dealerGroup._id, originalData.type, () => {
      setIsSubmitting(false);
      setRefreshKey(prev => prev + 1);
      onClose();
    });
    setOpenDeleteConfirm(false);
  };

  const handleCancelEdit = () => {
    setDealerGroup(originalData);
    setDealersForGroup(originalDealersForGroup); // reset dealers list too
    setIsEditing(false);
    setError('');
  };

  const handleListUpdate = updatedDealers => {
    setDealerGroup(prev => ({
      ...prev,
      dealers: updatedDealers.map(dealer => dealer.id),
    }));
  };

  const convertedDealers = useCallback(dealers => {
    if (!Array.isArray(dealers)) return [];
    return dealers
      .filter(item => item && item._id) // Filter out null/undefined items
      .map(item => ({
        id: item._id?.toString() || '',
        label: item.name || '',
      }))
      .filter(item => item.id); // Filter out items without valid IDs
  }, []);

  // Helper function to get user initials
  const getUserInitials = (firstName: string, lastName: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last;
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '1000px',
          maxWidth: '95vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Dealer Group Details
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isEditing && (
                <IconButton
                  onClick={() => {
                    setIsEditing(true);
                    setOriginalData(dealerGroup);
                  }}
                  sx={{ color: 'white', p: 0.5 }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {!isEditing && (
                <Tooltip title="Delete Group">
                  <IconButton
                    onClick={() => setOpenDeleteConfirm(true)}
                    sx={{ color: 'white', p: 0.5 }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{ color: 'white', p: 0.5 }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Name Field (now only the name input/title) */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {isEditing ? (
            <TextField
              name="name"
              variant="outlined"
              size="small"
              value={dealerGroup.name || ''}
              onChange={handleChange}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontWeight: 600,
                  fontSize: '1.1rem',
                },
              }}
              placeholder="Enter dealer group name"
            />
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight="600"
              sx={{ fontSize: '1.1rem' }}
            >
              {dealerGroup.name || 'Unnamed Group'}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          sx={{
            p: 3,
            flex: 1,
            overflowY: 'auto',
          }}
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Group Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="type"
                  value={dealerGroup.type || 'LIST'}
                  onChange={handleChange}
                  disabled={true}
                  sx={{ height: '42px' }}
                >
                  <MenuItem value="GROUP">Group</MenuItem>
                  <MenuItem value="LIST">List</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Business Dashboard URL for GROUP type (moved directly below type) */}
            {dealerGroup.type === 'GROUP' && (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Business Dashboard URL
                </Typography>
                {isEditing ? (
                  <TextField
                    name="business_dashboard_url"
                    variant="outlined"
                    size="small"
                    value={dealerGroup.settings?.business_dashboard_url || ''}
                    onChange={e => {
                      const value = e.target.value;
                      setDealerGroup(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          business_dashboard_url: value,
                        },
                      }));
                      setError('');
                    }}
                    fullWidth
                    placeholder="Enter business dashboard URL"
                  />
                ) : (
                  <Typography variant="body2">
                    {dealerGroup.settings?.business_dashboard_url || (
                      <span style={{ color: '#888' }}>Not set</span>
                    )}
                  </Typography>
                )}
              </Grid>
            )}

            {/* Metadata section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="subtitle2"
                color="text.primary"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                System Information
              </Typography>
            </Grid>

            {/* Created & Updated At (Read-Only) */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5,
                  gap: 0.5,
                }}
              >
                <HistoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.primary">
                  Created At
                </Typography>
              </Box>
              <Typography variant="body2">
                {formatToLocalTime(dealerGroup.created_at)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5,
                  gap: 0.5,
                }}
              >
                <HistoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.primary">
                  Updated At
                </Typography>
              </Box>
              <Typography variant="body2">
                {formatToLocalTime(dealerGroup.updated_at)}
              </Typography>
            </Grid>

            {/* Users Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.5 }}
              >
                <PeopleAltIcon fontSize="small" color="primary" />
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ fontWeight: 600 }}
                >
                  Assigned Users
                </Typography>
                {usersLoading && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>

              {usersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {usersError}
                </Alert>
              )}

              {usersLoading ? (
                <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : groupUsers.length > 0 ? (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    borderRadius: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.01)',
                  }}
                >
                  {console.log('Rendering users, groupUsers:', groupUsers)}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {groupUsers.map((user, index) => (
                      <Box
                        key={user._id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: theme.palette.primary.main,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                          }}
                        >
                          {getUserInitials(user.first_name, user.last_name)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.primary">
                            {user.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {user.roles?.map((role, roleIndex) => (
                            <Chip
                              key={roleIndex}
                              label={role}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.01)',
                  }}
                >
                  <PeopleAltIcon
                    sx={{
                      fontSize: 48,
                      color: 'text.disabled',
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    No users found for this dealer group
                  </Typography>
                </Paper>
              )}
            </Grid>

            {/* Dealers Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.5 }}
              >
                <StoreIcon fontSize="small" color="primary" />
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ fontWeight: 600 }}
                >
                  Assigned Dealers
                </Typography>
              </Box>

              {isEditing ? (
                dealers && dealersForGroup ? (
                  <Box sx={{ mt: 1 }}>
                    <DualListMAIQ
                      key={`dual-list-${dealers?.length || 0}`}
                      availableItems={convertedDealers(
                        dealers?.filter(
                          dealer =>
                            !dealersForGroup?.some(
                              groupDealer => groupDealer?._id === dealer?._id,
                            ),
                        ) || [],
                      )}
                      selectedItems={convertedDealers(dealersForGroup || [])}
                      onUpdate={handleListUpdate}
                    />
                  </Box>
                ) : (
                  <Box sx={{ height: 350 }}>
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      width="100%"
                      height="100%"
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                )
              ) : (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    borderRadius: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.01)',
                  }}
                >
                  {dealersForGroup && dealersForGroup.length > 0 ? (
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {dealersForGroup.map((dealer, index) => (
                        <Box
                          component="li"
                          key={dealer._id || index}
                          sx={{
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'text.primary',
                          }}
                        >
                          <StoreIcon
                            fontSize="small"
                            sx={{ color: 'action.active', opacity: 0.6 }}
                          />
                          <Typography variant="body2">{dealer.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      No dealers assigned to this group
                    </Typography>
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons - fixed at bottom when editing */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              zIndex: 9,
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
                borderColor: 'divider',
                py: 0.75,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenConfirm(true)}
              disabled={isSubmitting || isSame}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                py: 0.75,
                px: 3,
                '&:hover': {
                  bgcolor: '#0a1f3a',
                },
              }}
            >
              Save Changes{' '}
              {isSubmitting && (
                <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
              )}
            </Button>
          </Box>
        )}

        {/* Confirmation Modals */}
        <ConfirmationDialogMAIQ
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleEditSubmit}
          message="Are you sure you want to save the changes?"
        />
        <ConfirmationDialogMAIQ
          open={openDeleteConfirm}
          onClose={() => setOpenDeleteConfirm(false)}
          onConfirm={handleDeleteDealerGroup}
          message="Are you sure you want to delete this dealer group?"
        />
      </Paper>
    </ModalMAIQ>
  );
};

export default DealerGroupDetailModal;
